import * as Sentry from "@sentry/nextjs";
import { useToast } from "components/atoms/toast/toast-context";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { registerStudent } from "services/api/users/user-services";
import { eventNames } from "services/mixpanel/events";
import trackEvent from "services/mixpanel/mixpanel";
import { useAuth } from "stores/auth/auth-context";
import { FormDataState, UseUserDetailsReturn } from "./use-user-details.types";

export const useUserDetails = (): UseUserDetailsReturn => {
  const router = useRouter();
  const { addToast } = useToast();
  const {
    user,
    user: { userData }
  } = useAuth();
  const [formData, setFormData] = useState<FormDataState | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { access_token } = userData.primary_user;

  useEffect(() => {
    if (userData.primary_user.profile_state === "COMPLETE") {
      router.push("/bimbel");
    }
  }, [userData.primary_user]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedData = sessionStorage.getItem("updateUserFormData");

      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          setFormData(parsedData);
        } catch (error) {
          console.error("Error parsing stored form data:", error);
        }
      }
    }
  }, []);

  const student = user.userData?.primary_user;
  const parent = user.userData?.parent_user;

  const studentData = formData?.userData
    ? {
        phone: student.phone_number,
        email: formData.userData.email,
        name: formData.userData.name,
        grade: formData.userData.grade,
        curriculum: formData.userData.curriculum,
        location: formData.userData.residence,
        school: formData.userData.schoolName
      }
    : {
        phone: student.phone_number,
        email: student.email,
        name: student.full_name,
        grade: student.grade || "",
        curriculum: student.curriculum || "",
        location: student.city || "",
        school: student.school || ""
      };

  const parentData = formData?.parentData
    ? {
        phone: formData.parentData.parentPhone,
        name: formData.parentData.parentName
      }
    : {
        phone: parent?.phone_number ?? "",
        name: parent?.name ?? ""
      };

  const handleUpdateUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await registerStudent(
        {
          grade: student.grade?.trim(),
          city: studentData.location?.trim(),
          fullName: studentData.name?.trim(),
          email: studentData.email?.trim(),
          school: studentData.school?.trim(),
          curriculum: student.curriculum?.trim(),
          phoneNumber: studentData.phone?.trim(),
          class: student.class_type?.trim(),
          parentName: parentData.name?.trim(),
          parentPhoneNumber: parentData.phone?.trim()
        },
        access_token
      );

      if (response.message === "OK") {
        setIsSuccess(true);
        trackEvent({
          eventName: eventNames.studentRegister,
          payload: {
            eventSource: "website"
          }
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        variant: "error",
        heading:
          JSON.parse((error as Error).message).message ??
          "Error updating user profile",
        duration: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = () => {
    if (typeof window !== "undefined") {
      const originalDataToStore = {
        userData: {
          name: student.full_name,
          email: student.email,
          grade: student.grade || "",
          curriculum: student.curriculum || "",
          residence: student.city || "",
          schoolName: student.school || ""
        },
        parentData: {
          parentName: parent?.name ?? "",
          parentPhone: parent?.phone_number ?? ""
        }
      };

      sessionStorage.setItem(
        "originalUserFormData",
        JSON.stringify(originalDataToStore)
      );
    }
    router.push("/user/register/update-user-details");
  };

  return {
    studentData,
    parentData,
    handleUpdateUserProfile,
    handleUpdate,
    isSuccess,
    isLoading
  };
};
